import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useTranslations } from "@/lib/translations";
import { isWorkEmail } from "@workspace/email-policy";
import { Send } from "lucide-react";
import CaptchaField from "@/components/CaptchaField";

export type LeadSource =
  | "pro_book_demo"
  | "individual_purchase_report"
  | "contact_page";

export interface LeadContextItem {
  label: string;
  value: string;
}

interface LeadFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: LeadSource;
  title: string;
  description?: string;
  contextItems?: LeadContextItem[];
  contextPayload?: Record<string, unknown>;
}

interface FormState {
  name: string;
  email: string;
  company: string;
  phone: string;
  message: string;
  website: string;
}

const EMPTY: FormState = {
  name: "",
  email: "",
  company: "",
  phone: "",
  message: "",
  website: "",
};

export function LeadFormModal({
  open,
  onOpenChange,
  source,
  title,
  description,
  contextItems,
  contextPayload,
}: LeadFormModalProps) {
  const { t, language, isRTL } = useTranslations();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const prevOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!open) {
      setError(null);
      setSubmitting(false);
      setCaptchaToken("");
      setCaptchaAnswer("");
      return;
    }
    // Only fire on the false -> true transition, not on every render
    // (e.g. a language switch while the popup is open must not duplicate).
    if (wasOpen) return;
    if (typeof window === "undefined") return;
    const virtual =
      source === "pro_book_demo"
        ? { path: "/popup/book-demo", title: "Book a demo - ESG Invest" }
        : source === "individual_purchase_report"
          ? { path: "/popup/purchase-report", title: "Purchase ESG report - ESG Invest" }
          : null;
    if (!virtual) return;
    const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
    w.dataLayer = w.dataLayer ?? [];
    w.dataLayer.push({
      event: "virtual_pageview",
      page_path: virtual.path,
      page_title: virtual.title,
      page_location:
        typeof window.location !== "undefined"
          ? window.location.origin + virtual.path
          : virtual.path,
      locale: language,
    });
  }, [open, source, language]);

  const update = (field: keyof FormState, value: string) =>
    setForm((p) => ({ ...p, [field]: value }));

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const isFormValid =
    form.name.trim() !== "" &&
    isValidEmail(form.email) &&
    form.company.trim() !== "" &&
    form.phone.trim() !== "" &&
    captchaToken.trim() !== "" &&
    captchaAnswer.trim() !== "";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isWorkEmail(form.email)) {
      setError(t("lead.form.workEmailRequired"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || null,
          phone: form.phone.trim() || null,
          message: form.message.trim() || null,
          source,
          locale: language,
          context: contextPayload ?? null,
          website: form.website,
          captchaToken,
          captchaAnswer: captchaAnswer.trim(),
        }),
      });
      if (!res.ok) {
        let reason: string | undefined;
        try {
          const body = (await res.json()) as { reason?: string };
          reason = body.reason;
        } catch {
          /* ignore */
        }
        if (reason && reason.startsWith("captcha")) {
          setCaptchaToken("");
          setCaptchaAnswer("");
          throw new Error(t("lead.form.captchaWrong"));
        }
        if (reason === "email_not_work") {
          throw new Error(t("lead.form.workEmailRequired"));
        }
        throw new Error(t("lead.toast.errorDesc"));
      }

      if (typeof window !== "undefined") {
        // Marketing analytics contract uses short source values
        // (`pro_card`, `individual_report`, `contact_page`) rather than the
        // longer DB enum values stored against the lead row.
        const gtmSource =
          source === "pro_book_demo"
            ? "pro_card"
            : source === "individual_purchase_report"
              ? "individual_report"
              : source;
        const w = window as unknown as {
          dataLayer?: Array<Record<string, unknown>>;
        };
        w.dataLayer = w.dataLayer ?? [];
        w.dataLayer.push({
          event: "form_lead_submitted",
          source: gtmSource,
          locale: language,
        });
      }

      toast({
        title: t("lead.toast.successTitle"),
        description: t("lead.toast.successDesc"),
      });
      setForm(EMPTY);
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t("lead.toast.errorDesc");
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {contextItems && contextItems.length > 0 && (
          <div
            className="rounded-md border border-border bg-muted/40 p-3 text-sm space-y-1"
            data-testid="lead-context"
          >
            {contextItems.map((item, idx) => (
              <div key={idx} className="flex justify-between gap-3">
                <span className="text-muted-foreground">{item.label}</span>
                <span className="font-medium text-foreground text-end">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="lead-name">
              {t("lead.form.name")} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="lead-name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              required
              maxLength={200}
              data-testid="input-lead-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-email">
              {t("lead.form.email")} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="lead-email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              required
              maxLength={320}
              data-testid="input-lead-email"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="lead-company">
                {t("lead.form.company")} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="lead-company"
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                required
                maxLength={200}
                data-testid="input-lead-company"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lead-phone">
                {t("lead.form.phone")} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="lead-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                required
                maxLength={50}
                data-testid="input-lead-phone"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="lead-message">{t("lead.form.message")}</Label>
            <Textarea
              id="lead-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              maxLength={4000}
              rows={4}
              placeholder={t("lead.form.messagePlaceholder")}
              data-testid="input-lead-message"
            />
          </div>

          <CaptchaField
            token={captchaToken}
            onTokenChange={setCaptchaToken}
            answer={captchaAnswer}
            onAnswerChange={setCaptchaAnswer}
            disabled={submitting}
          />

          {/* Honeypot */}
          <div className="hidden" aria-hidden="true">
            <label>
              Website
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => update("website", e.target.value)}
              />
            </label>
          </div>

          {error && (
            <p
              className="text-sm text-destructive"
              role="alert"
              data-testid="lead-error"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            variant="accent"
            size="lg"
            className="w-full"
            disabled={submitting || !isFormValid}
            data-testid="button-lead-submit"
          >
            <Send className={`w-4 h-4 ${isRTL ? "ml-2" : "mr-2"}`} />
            {submitting ? t("lead.form.submitting") : t("lead.form.submit")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default LeadFormModal;
